import { apiBase, fetchUsingApi } from "@/api/rest/core/apiClient";
import { ApiResponse } from "@/api/rest/core/types";
import {
  AddQuestionMessageRequest,
  Question,
  QuestionMessage,
  QuestionMessageRaw,
  QuestionRaw,
  QuestionResponse,
  questionStatusMap,
  UpdateQuestionStatusRequest,
} from "@/api/rest/models/question";
import dayjs from "@/components/dayjsExtended";

import { appendUploadsToFormData, UploadedFile } from "./uploadService";

const enrichQuestionResponse = (response: QuestionResponse): QuestionResponse => {
  return {
    ...response,
    question: enrichQuestion([response.question])[0],
    messages: enrichQuestionMessages(response.messages),
  };
};

const enrichQuestionMessage = (m: QuestionMessageRaw): QuestionMessage => {
  return {
    ...m,
    createdAtTimestamp: dayjs(m.createdAt),
  };
};

const enrichQuestionMessages = (messages: QuestionMessageRaw[]): QuestionMessage[] => {
  return messages.map(enrichQuestionMessage);
};

const enrichQuestion = (questions: QuestionRaw[]): Question[] => {
  return questions.map((q) => ({
    ...q,
    createdAtTimestamp: dayjs(q.createdAt),
    localisedStatus: questionStatusMap.get(q.status) ?? q.status.toString(),
  }));
};

export const getAllQuestions = async (householdId: number): Promise<ApiResponse<Question[]>> => {
  return await fetchUsingApi(
    `${apiBase}/api/v1/question-escalations?householdId=${householdId}`,
    "GET",
    enrichQuestion,
  );
};

export const getAllQuestionsForBuilding = async (buildingId: number): Promise<ApiResponse<Question[]>> => {
  return await fetchUsingApi(
    `${apiBase}/api/v1/question-escalations/list/building?buildingId=${buildingId}`,
    "GET",
    enrichQuestion,
  );
};

export const getQuestion = async (questionId: number): Promise<ApiResponse<QuestionResponse>> => {
  return await fetchUsingApi(`${apiBase}/api/v1/question-escalations/${questionId}`, "GET", enrichQuestionResponse);
};

export const sendMessage = async (
  questionId: number,
  request: AddQuestionMessageRequest,
): Promise<ApiResponse<QuestionMessage>> => {
  return await fetchUsingApi(
    `${apiBase}/api/v1/question-escalations/${questionId}/message`,
    "POST",
    enrichQuestionMessage,
    JSON.stringify(request),
  );
};

export const sendFiles = async (
  questionId: number,
  accountId: number,
  uploads: UploadedFile[],
): Promise<ApiResponse<QuestionMessage[]>> => {
  const formData = new FormData();
  formData.append("accountId", accountId.toString());
  appendUploadsToFormData(formData, uploads);
  return await fetchUsingApi(
    `${apiBase}/api/v1/question-escalations/${questionId}/files`,
    "POST",
    enrichQuestionMessages,
    formData,
  );
};

export const updateStatus = async (
  questionId: number,
  request: UpdateQuestionStatusRequest,
): Promise<ApiResponse<QuestionRaw>> => {
  return await fetchUsingApi(
    `${apiBase}/api/v1/question-escalations/${questionId}/status`,
    "PUT",
    (e) => e as QuestionRaw,
    JSON.stringify(request),
  );
};
